import React, { createContext, useState, useContext, useEffect } from "react";
import {
  loginCustomerService,
  registerCustomerService,
  quickLoginService,
  getCustomerProfileService,
  sendWhatsAppOtpService,
  whatsappLoginService,
} from "../services/resellerCustomer.service";

const ResellerCustomerContext = createContext();

export const useCustomer = () => {
  const context = useContext(ResellerCustomerContext);
  if (!context) {
    throw new Error("useCustomer must be used within ResellerCustomerProvider");
  }
  return context;
};

export const ResellerCustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(() => {
    // Default to guest mode, but check localStorage
    return localStorage.getItem("customerMode") || "guest";
  });

  // Load customer from localStorage on mount
  useEffect(() => {
    const loadCustomer = async () => {
      const token = localStorage.getItem("customerAuthToken");
      const savedCustomer = localStorage.getItem("customerData");

      if (token && savedCustomer) {
        try {
          // Verify token is still valid by fetching profile
          const response = await getCustomerProfileService();
          if (response.data.success) {
            setCustomer(response.data.customer);
            setIsAuthenticated(true);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem("customerAuthToken");
            localStorage.removeItem("customerData");
          }
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem("customerAuthToken");
          localStorage.removeItem("customerData");
        }
      }
      setLoading(false);
    };

    loadCustomer();
  }, []);

  const login = async (resellerUuid, email, password, fingerprint) => {
    try {
      const response = await loginCustomerService(
        resellerUuid,
        email,
        password,
        fingerprint,
      );
      if (response.data.success) {
        const { customer, token } = response.data;
        localStorage.setItem("customerAuthToken", token);
        localStorage.setItem("customerData", JSON.stringify(customer));
        setCustomer(customer);
        setIsAuthenticated(true);
        return { success: true, customer };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  };

  const register = async (resellerUuid, customerData, fingerprint) => {
    try {
      const response = await registerCustomerService(resellerUuid, {
        ...customerData,
        fingerprint,
      });
      if (response.data.success) {
        const { customer, token } = response.data;
        localStorage.setItem("customerAuthToken", token);
        localStorage.setItem("customerData", JSON.stringify(customer));
        setCustomer(customer);
        setIsAuthenticated(true);
        return { success: true, customer };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Registration failed",
      };
    }
  };

  const quickLogin = async (resellerUuid, name, phone, fingerprint) => {
    try {
      const response = await quickLoginService(
        resellerUuid,
        name,
        phone,
        fingerprint,
      );
      if (response.data.success) {
        const { customer, token } = response.data;
        localStorage.setItem("customerAuthToken", token);
        localStorage.setItem("customerData", JSON.stringify(customer));
        setCustomer(customer);
        setIsAuthenticated(true);
        switchToAccountMode();
        return { success: true, customer, message: response.data.message };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Quick login failed",
      };
    }
  };

  const sendWhatsAppOtp = async (phone, userName) => {
    try {
      const response = await sendWhatsAppOtpService(phone, userName);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to send WhatsApp OTP",
      };
    }
  };

  const whatsappLogin = async (resellerUuid, loginData) => {
    try {
      const response = await whatsappLoginService(resellerUuid, loginData);
      if (response.data.success) {
        const { customer, token } = response.data;
        localStorage.setItem("customerAuthToken", token);
        localStorage.setItem("customerData", JSON.stringify(customer));
        setCustomer(customer);
        setIsAuthenticated(true);
        switchToAccountMode();
        return { success: true, customer, message: response.data.message };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "WhatsApp login failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("customerAuthToken");
    localStorage.removeItem("customerData");
    setCustomer(null);
    setIsAuthenticated(false);
    switchToGuestMode();
  };

  const switchToAccountMode = () => {
    setMode("account");
    localStorage.setItem("customerMode", "account");
  };

  const switchToGuestMode = () => {
    setMode("guest");
    localStorage.setItem("customerMode", "guest");
  };

  const updateCustomerData = (updatedCustomer) => {
    setCustomer(updatedCustomer);
    localStorage.setItem("customerData", JSON.stringify(updatedCustomer));
  };

  const value = {
    customer,
    isAuthenticated,
    loading,
    mode,
    login,
    register,
    quickLogin,
    sendWhatsAppOtp,
    whatsappLogin,
    logout,
    switchToAccountMode,
    switchToGuestMode,
    updateCustomerData,
  };

  return (
    <ResellerCustomerContext.Provider value={value}>
      {children}
    </ResellerCustomerContext.Provider>
  );
};
